import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { devices } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Sparkline } from '@/components/ui/Sparkline';
import { relTime, uptime } from '@/lib/format';
import { Search, Filter, Download, Hand, X, Wifi, WifiOff, Plug, CheckCircle2, User, AlertTriangle, Eye } from 'lucide-react';
import type { Device } from '@/lib/api';

export default function DevicesList() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'online' | 'unsync' | 'offline' | 'unlinked'>('all');
  const [claimOpen, setClaimOpen] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 50;

  const params: Record<string, unknown> = { per_page: perPage, page };
  if (q) params.q = q;
  if (filter === 'online') params.reach_state = 'online';
  if (filter === 'unsync') params.reach_state = 'unsync';
  if (filter === 'offline') params.reach_state = 'offline';
  if (filter === 'unlinked') params.unlinked = true;

  const { data, isLoading } = useQuery({
    queryKey: ['devices', q, filter, page],
    queryFn: () => devices.list(params),
  });
  const list = data?.data ?? [];
  const total = data?.total ?? 0;
  const lastPage = data?.last_page ?? 1;

  // Reset to page 1 when filter/search change
  const filterKey = `${q}|${filter}`;
  useEffect(() => { setPage(1); }, [filterKey]);

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle={`${data?.total ?? 0} total`}
        actions={
          <>
            <button className="btn btn-sm" onClick={() => setClaimOpen(true)}>
              <Hand size={11} />Réclamer un CPE
            </button>
            <button className="btn btn-sm"><Filter size={11} />Filtres</button>
            <button className="btn btn-sm"><Download size={11} />Export CSV</button>
          </>
        }
      />
      {claimOpen && <ClaimModal onClose={() => setClaimOpen(false)} />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Sticky top: search + filter chips + legend + table header */}
        <div className="flex-shrink-0 bg-bg-2">
          <div className="p-4 border-b border-border flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint" />
              <input
                className="input pl-7 w-full"
                placeholder="Rechercher serial, MAC, customer…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1 bg-bg-2 border border-border rounded">
              {[
                ['all', 'Tous', 'text-text-muted'],
                ['online', 'Online', 'text-success'],
                ['unsync', 'Unsync ⚠', 'text-warning'],
                ['offline', 'Offline', 'text-danger'],
                ['unlinked', 'Orphelins', 'text-text-muted'],
              ].map(([k, label, col]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k as 'all' | 'online' | 'unsync' | 'offline' | 'unlinked')}
                  className={`h-7 px-3 text-xs ${filter === k ? `bg-surface-hi ${col}` : 'text-text-muted hover:text-text'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <StatusLegend />
        </div>

        {/* Scrollable table body */}
        <div className="flex-1 overflow-auto">
        <table className="luc-table">
          <thead className="sticky top-0 bg-bg-2 z-10">
            <tr>
              <th style={{ width: 110 }}>Status</th>
              <th>Serial</th>
              <th>Modèle</th>
              <th>Customer</th>
              <th>Firmware</th>
              <th>Uptime</th>
              <th>WAN IP</th>
              <th>Last inform</th>
              <th>Bandwidth 24h</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id}>
                <td>
                  <StatusBadges device={d} />
                </td>
                <td>
                  <Link to={`/app/devices/${d.id}`} className="font-mono text-[12px] hover:text-brand">
                    {d.serial_number}
                  </Link>
                </td>
                <td className="text-text-muted text-xs">
                  {(d.metadata as Record<string, unknown> | undefined)?.hardware_model as string
                    ?? d.model?.model_name
                    ?? '—'}
                </td>
                <td>
                  {d.customer?.name ?? <span className="text-text-faint italic">non lié</span>}
                </td>
                <td className="font-mono text-[12px] text-text-muted">{d.firmware_version ?? '—'}</td>
                <td className="text-xs text-text-muted">{uptime(d.uptime_seconds)}</td>
                <td className="font-mono text-[12px] text-text-muted">
                  {d.metadata?.is_nat
                    ? <span title={`Privée CPE: ${wanIp(d.connection_request_url)}\nPublique (vue filos): ${d.metadata?.public_wan_ip ?? '—'}`}>
                        <span className="text-orange-400">{d.metadata?.public_wan_ip ?? '—'}</span>
                        <span className="text-text-faint text-[10px] ml-1">NAT</span>
                      </span>
                    : wanIp(d.connection_request_url)}
                </td>
                <td className="font-mono text-[12px] text-text-muted">{relTime(d.last_inform_at)}</td>
                <td><Sparkline values={realSpark(d.bandwidth_24h)} /></td>
                <td>
                  <Link to={`/app/devices/${d.id}`} className="btn btn-ghost btn-sm">Détail →</Link>
                </td>
              </tr>
            ))}
            {!isLoading && list.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-text-faint">Aucun device trouvé.</td></tr>
            )}
            {isLoading && (
              <tr><td colSpan={9} className="text-center py-12 text-text-faint">Chargement…</td></tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Sticky bottom: pagination */}
        <div className="flex-shrink-0 border-t border-border bg-bg-2 px-4 py-2 flex items-center justify-between text-xs">
          <div className="text-text-muted">
            <span className="text-text">{((page - 1) * perPage) + 1}</span>
            <span className="text-text-faint mx-1">–</span>
            <span className="text-text">{Math.min(page * perPage, total)}</span>
            <span className="text-text-faint mx-1">sur</span>
            <span className="text-text font-semibold">{total}</span>
            <span className="text-text-faint ml-1">devices</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-text-faint">Page {page} / {lastPage}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page <= 1}
                className="btn btn-ghost btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                title="Première page"
              >«</button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-ghost btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
              >‹ Préc</button>
              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
                className="btn btn-ghost btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
              >Suiv ›</button>
              <button
                onClick={() => setPage(lastPage)}
                disabled={page >= lastPage}
                className="btn btn-ghost btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                title="Dernière page"
              >»</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Légende des icônes Status — affichée sous le tableau pour onboarder.
function StatusLegend() {
  const items: Array<[typeof Wifi, string, string]> = [
    [Wifi,           '#22c55e', 'Online'],
    [WifiOff,        '#ef4444', 'Offline'],
    [Eye,            '#eab308', 'Discovery (pas encore docké)'],
    [Plug,           '#3b82f6', 'Docké (Phase 1 — communication ACS établie)'],
    [CheckCircle2,   '#10b981', 'Provisioned (Phase 2 — config customer appliquée)'],
    [User,           '#a78bfa', 'Linké à un customer'],
    [AlertTriangle,  '#f97316', 'Tenant bootstrap (à reviewer manuellement)'],
  ];
  return (
    <div className="px-4 py-2 border-b border-border bg-bg-2 flex items-center gap-5 flex-wrap text-[10px] text-text-muted">
      <span className="uppercase tracking-wide text-text-faint">Légende :</span>
      {items.map(([Icon, color, label], i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <Icon size={12} strokeWidth={2.25} style={{ color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

// Status icon cluster pour un device — pictogrammes empilés avec tooltip natif.
function StatusBadges({ device: d }: { device: Device }) {
  const state = d.bootstrap_state;
  const isProvisioned = state === 'provisioned' || !!d.provisioned_at;
  const isDocked      = !isProvisioned && (state === 'adopted' || !!d.adopted_at);
  const isDiscovery   = !isDocked && !isProvisioned && (state === 'discovered' || state === 'bootstrapped' || !state);
  const isBootstrapTenant = d.tenant?.slug === 'bootstrap';
  const isLinked      = d.customer_id !== null;

  const Badge = ({ icon: Icon, color, title }: { icon: typeof Wifi; color: string; title: string }) => (
    <span title={title} className="inline-flex" style={{ color }}>
      <Icon size={13} strokeWidth={2.25} />
    </span>
  );

  return (
    <div className="flex items-center gap-1.5">
      {d.is_online
        ? <Badge icon={Wifi}    color="#22c55e" title="Online" />
        : <Badge icon={WifiOff} color="#ef4444" title="Offline" />}

      {isDiscovery   && <Badge icon={Eye}          color="#eab308" title="Discovery (pas encore docké)" />}
      {isDocked      && <Badge icon={Plug}         color="#3b82f6" title="Docké (Phase 1) — communication ACS établie" />}
      {isProvisioned && <Badge icon={CheckCircle2} color="#10b981" title="Provisioned (Phase 2) — config customer appliquée" />}

      {isLinked && <Badge icon={User} color="#a78bfa" title={`Linked: ${d.customer?.name ?? '—'}`} />}

      {isBootstrapTenant && <Badge icon={AlertTriangle} color="#f97316" title="Tenant 'bootstrap' — à reviewer" />}
    </div>
  );
}

// Parse WAN IP depuis connection_request_url ex: "http://199.4.190.244:30005/"
function wanIp(url?: string | null): string {
  if (!url) return '—';
  const m = url.match(/\d+\.\d+\.\d+\.\d+/);
  return m ? m[0] : '—';
}

// Real sparkline from API : 24 hourly avg rate_in_bps points.
// API retourne array de {h: epoch, in: bps, out: bps}. On extrait in_bps,
// pad les heures manquantes avec 0, normalize en Mbps.
function realSpark(rows?: Array<{ h: number; in: number; out: number }>): number[] {
  if (!rows || rows.length === 0) return [];
  const nowH = Math.floor(Date.now() / 1000 / 3600);
  const byHour: Record<number, number> = {};
  rows.forEach((r) => { byHour[Math.floor(r.h / 3600)] = r.in; });
  return Array.from({ length: 24 }, (_, i) => {
    const h = nowH - 23 + i;
    return (byHour[h] ?? 0) / 1_000_000;  // bps → Mbps
  });
}

// ─── Réclamer un CPE bloqué dans bootstrap (oubli subnet, etc.) ──────────────
function ClaimModal({ onClose }: { onClose: () => void }) {
  const [serial, setSerial] = useState('');
  const [mac, setMac] = useState('');
  const qc = useQueryClient();
  const nav = useNavigate();

  const m = useMutation({
    mutationFn: () => devices.claim({
      serial_number: serial.trim() || undefined,
      mac_address:   mac.trim() || undefined,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      nav(`/app/devices/${r.device_id}`);
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={onClose}>
      <div className="bg-bg border border-border rounded shadow-xl w-[440px] max-w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text">Réclamer un CPE</h2>
          <button onClick={onClose} className="text-text-faint hover:text-text"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-text-muted leading-relaxed">
            Si un CPE de votre tenant est tombé dans <span className="font-mono">bootstrap</span> (subnet
            non configuré), entrez son numéro de série OU son adresse MAC pour le récupérer. Le dock se fera automatiquement.
          </p>
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Numéro de série</label>
            <input className="input w-full font-mono" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="CXNK00654191" />
          </div>
          <div className="text-center text-[11px] text-text-faint">— ou —</div>
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Adresse MAC</label>
            <input className="input w-full font-mono" value={mac} onChange={(e) => setMac(e.target.value)} placeholder="aa:bb:cc:dd:ee:ff" />
          </div>
          {m.isError && (
            <div className="text-xs text-red-400">
              {(m.error as { response?: { status?: number } })?.response?.status === 404
                ? 'Aucun CPE trouvé dans le sandbox bootstrap avec cet identifiant.'
                : 'Erreur. Réessayez.'}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-sm" onClick={onClose}>Annuler</button>
            <button
              className="btn btn-sm btn-primary"
              disabled={(!serial.trim() && !mac.trim()) || m.isPending}
              onClick={() => m.mutate()}
            >
              {m.isPending ? 'Recherche…' : 'Réclamer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
